import React, { useEffect } from "react";
import styled from "styled-components";
import { Button } from "../../atoms/Button";

type Props = {
  isOpen: boolean;
  onConfirm: () => void;
};

export const SessionExpiredModal: React.FC<Props> = ({
  isOpen,
  onConfirm
}) => {
  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      // Store original body and document element styles
      const body = document.body;
      const documentElement = document.documentElement;

      const originalBodyOverflow = body.style.overflow;
      const originalBodyPosition = body.style.position;
      const originalBodyTop = body.style.top;
      const originalBodyWidth = body.style.width;
      const originalDocumentOverflow = documentElement.style.overflow;

      // Get current scroll position
      const scrollY = window.scrollY;
      const scrollBarWidth = window.innerWidth - documentElement.clientWidth;

      // Apply comprehensive scroll lock
      body.style.overflow = 'hidden';
      body.style.position = 'fixed';
      body.style.top = `-${scrollY}px`;
      body.style.width = '100%';
      body.style.paddingRight = `${scrollBarWidth}px`;
      documentElement.style.overflow = 'hidden';

      // Prevent touch scrolling on iOS - but only outside modal content
      const preventTouchMove = (e: TouchEvent) => {
        // Allow scrolling within modal content
        const target = e.target as Element;
        const modalContent = document.querySelector('[data-session-modal-content]');
        if (modalContent && modalContent.contains(target)) {
          return; // Allow scrolling within modal
        }
        e.preventDefault();
      };

      document.addEventListener('touchmove', preventTouchMove, { passive: false });

      // Cleanup function to restore original state
      return () => {
        body.style.overflow = originalBodyOverflow;
        body.style.position = originalBodyPosition;
        body.style.top = originalBodyTop;
        body.style.width = originalBodyWidth;
        body.style.paddingRight = '';
        documentElement.style.overflow = originalDocumentOverflow;

        document.removeEventListener('touchmove', preventTouchMove);

        // Restore scroll position
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <StyledModalBackground>
      <StyledModalContainer data-session-modal-content>
        <StyledHeader>
          <StyledTitleWrapper>
            <StyledModalTitle>
              Session Expired
            </StyledModalTitle>
            <StyledModalSubTitle>
              Please log in again to continue using the application.
            </StyledModalSubTitle>
          </StyledTitleWrapper>
        </StyledHeader>

        <StyledFooter>
          <Button
            text="Log In Again"
            variant="filled"
            onClick={onConfirm}
          />
        </StyledFooter>
      </StyledModalContainer>
    </StyledModalBackground>
  );
};

const StyledModalBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledModalContainer = styled.div`
  width: 100%;
  max-width: 512px;
  padding: 28px 32px;
  border-radius: 8px;
  background-color: white;
  box-shadow: 4px 6px 10px 0px rgba(0, 0, 0, 0.1);
  animation: fadeIn 0.3s ease-in-out;

  @media (max-width: 768px) {
    max-width: 90%;
    padding: 24px 20px;
    border-radius: 6px;
  }

  @media (max-width: 480px) {
    max-width: 95%;
    padding: 20px 16px;
    border-radius: 4px;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: scale(0.9);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const StyledHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 32px;
  position: relative;

  @media (max-width: 768px) {
    margin-bottom: 24px;
  }

  @media (max-width: 480px) {
    margin-bottom: 20px;
  }
`;

const StyledTitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
`;

const StyledModalTitle = styled.div`
  max-width: 390px;
  font-size: 28px;
  font-weight: 600;
  line-height: 1.5;
  word-wrap: break-word;
  white-space: pre-line;
  color: #e74c3c;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    max-width: 300px;
    font-size: 24px;
    margin-bottom: 12px;
  }

  @media (max-width: 480px) {
    max-width: 250px;
    font-size: 20px;
    margin-bottom: 8px;
  }
`;

const StyledModalSubTitle = styled.div`
  max-width: 390px;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  word-wrap: break-word;
  white-space: pre-line;
  color: #666;

  @media (max-width: 768px) {
    max-width: 300px;
    font-size: 14px;
  }

  @media (max-width: 480px) {
    max-width: 250px;
    font-size: 13px;
  }
`;



const StyledFooter = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;

  @media (max-width: 768px) {
    gap: 12px;
  }

  @media (max-width: 480px) {
    gap: 8px;
  }
`;
